import {X} from 'lucide-react';
import {MarketWidget} from '../components/MarketWidget';
import {APP_BY_ID} from '../data/apps';
import {useDesktop} from '../desktop/DesktopContext';
import {AppIcon} from '../shell/AppIcon';
import type {AppDefinition} from '../types';
import {rememberLaunchOrigin} from './sheetMotion';

function HomeIcon({app, onOpen}: {app: AppDefinition; onOpen: () => void}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      // The app flies out of — and later back into — whichever launcher was
      // touched. `data-app-icon` is how `sheetMotion` finds one at all; the
      // pointer handler is how it knows WHICH one, when the same app sits in
      // the grid, in the dock and in the running list at the same time.
      data-app-icon={app.id}
      onPointerDown={(event) => rememberLaunchOrigin(app.id, event.currentTarget)}
      aria-label={app.name}
      className="mobile-home-icon tap flex flex-col items-center gap-2 rounded-card p-1"
    >
      <span className="h-[62px] w-[62px]">
        <AppIcon app={app} />
      </span>
      <span className="w-full truncate text-center text-[11px] font-semibold text-ink-2">
        {app.shortName ?? app.name}
      </span>
    </button>
  );
}

/**
 * The home screen.
 *
 * It replaces two desktop affordances that have no touch equivalent: the
 * magnifying dock (a hover-only interaction) and the minimised-window
 * thumbnails (a 40px drop target). Running apps are listed as full-width rows
 * instead, so resuming or closing one is a normal tap.
 */
export function MobileHome({title}: {title: string}) {
  const {apps, closeWindow, openApp, restoreWindow, windows} = useDesktop();

  const launchable = apps;
  const running = windows
    .map((win) => ({app: APP_BY_ID[win.appId], win}))
    .filter((entry): entry is {app: AppDefinition; win: (typeof windows)[number]} =>
      Boolean(entry.app),
    );

  const now = new Date();

  return (
    <div className="mobile-scroll relative z-10 flex-1 px-5 pb-40">
      <header className="pb-4 pt-2">
        <p className="text-[13px] font-semibold text-ink-3">
          {now.toLocaleDateString('vi-VN', {
            weekday: 'long',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}
        </p>
        <h1 className="mt-1 text-[26px] font-extrabold tracking-tight text-ink">{title}</h1>
      </header>

      {/* iOS Market Widget */}
      <section className="mb-6">
        <MarketWidget />
      </section>

      {running.length > 0 ? (
        <section className="mb-8">
          <h2 className="mb-3 text-[11px] font-bold uppercase tracking-[0.16em] text-ink-3">
            Đang mở
          </h2>
          <ul className="space-y-3">
            {running.map(({app, win}) => (
              <li key={win.id} className="surface flex items-center gap-3 rounded-card p-3">
                <button
                  type="button"
                  onClick={() => restoreWindow(win.id)}
                  data-app-icon={app.id}
                  onPointerDown={(event) => rememberLaunchOrigin(app.id, event.currentTarget)}
                  className="tap flex flex-1 items-center gap-3 rounded-control px-1 text-left"
                >
                  <span className="h-10 w-10 shrink-0">
                    <AppIcon app={app} />
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-[14px] font-bold text-ink">
                      {app.name}
                    </span>
                    <span className="block text-[11px] font-medium text-ink-3">
                      {win.minimized ? 'Đang chờ' : 'Đang mở'}
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  aria-label={`Đóng ${app.name}`}
                  onClick={() => closeWindow(win.id)}
                  className="tap flex items-center justify-center rounded-control text-ink-3 active:text-ink"
                >
                  <X size={18} />
                </button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section>
        <h2 className="mb-4 text-[11px] font-bold uppercase tracking-[0.16em] text-ink-3">
          Ứng dụng
        </h2>
        <div className="grid grid-cols-4 gap-x-3 gap-y-5">
          {launchable.map((app) => (
            <HomeIcon key={app.id} app={app} onOpen={() => openApp(app.id)} />
          ))}
        </div>
      </section>
    </div>
  );
}
